import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Users, 
  Shield,
  User,
  Mail,
  Calendar,
  UserPlus,
  Copy,
  Check,
  FolderKanban,
  ChevronDown,
  ChevronUp,
  Layers,
  Phone,
  Save,
  Pencil,
  Key,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  'Logistics': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Safety': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Financial': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Operations': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'HR': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'Admin': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Project Management': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'IT': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  'Business Development': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Engineering': 'bg-teal-500/20 text-teal-400 border-teal-500/30'
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'member', department_ids: [], category_access: [], password: '' });
  const [editingPhone, setEditingPhone] = useState({});
  const [savingPhone, setSavingPhone] = useState({});
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  
  // Edit user state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Reset password state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');  // Optional custom password
  
  // Delete user state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/categories`)
      ]);
      setUsers(usersRes.data);
      setCategories(categoriesRes.data.categories || []);
      
      // Try to fetch departments, but don't fail if it errors
      try {
        const departmentsRes = await axios.get(`${API}/departments`);
        setDepartments(departmentsRes.data);
      } catch (e) {
        setDepartments([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role?role=${newRole}`);
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, role: newRole, category_access: newRole === 'admin' ? categories : u.category_access } : u
      ));
      toast.success('Role updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleCategoryToggle = async (userId, category, currentCategories) => {
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];
    
    try {
      await axios.put(`${API}/users/${userId}/categories`, { category_access: newCategories });
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, category_access: newCategories } : u
      ));
      toast.success('Category access updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update categories');
    }
  };

  const handleDepartmentToggle = async (userId, departmentId, currentDeptIds) => {
    const newDeptIds = currentDeptIds.includes(departmentId)
      ? currentDeptIds.filter(d => d !== departmentId)
      : [...currentDeptIds, departmentId];
    
    try {
      await axios.put(`${API}/users/${userId}/departments`, { department_ids: newDeptIds });
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, department_ids: newDeptIds } : u
      ));
      toast.success('Department access updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update departments');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setInviteResult(null);

    try {
      const response = await axios.post(`${API}/users/invite`, inviteForm);
      setInviteResult(response.data);
      setUsers(prev => [...prev, {
        id: response.data.id,
        email: response.data.email,
        name: response.data.name,
        role: response.data.role,
        department_ids: inviteForm.department_ids,
        category_access: inviteForm.category_access,
        created_at: new Date().toISOString()
      }]);
      toast.success('User invited successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const copyCredentials = () => {
    if (inviteResult) {
      navigator.clipboard.writeText(`Email: ${inviteResult.email}\nTemporary Password: ${inviteResult.temporary_password}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Credentials copied to clipboard');
    }
  };

  const copyResetCredentials = () => {
    if (resetResult) {
      navigator.clipboard.writeText(`Email: ${resetResult.email}\nNew Temporary Password: ${resetResult.temporary_password}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('New password copied to clipboard');
    }
  };

  const resetInviteDialog = () => {
    setInviteForm({ email: '', name: '', role: 'member', department_ids: [], category_access: [], password: '' });
    setInviteResult(null);
    setInviteDialogOpen(false);
  };

  // Edit user functions
  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({ name: user.name || '', email: user.email });
    setEditDialogOpen(true);
  };

  const saveUserEdit = async () => {
    if (!editingUser) return;
    setSavingEdit(true);
    try {
      await axios.put(`${API}/users/${editingUser.id}`, editForm);
      setUsers(prev => prev.map(u => 
        u.id === editingUser.id ? { ...u, ...editForm } : u
      ));
      toast.success('User updated successfully');
      setEditDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingEdit(false);
    }
  };

  // Reset password functions
  const handleResetPassword = (user) => {
    setResetUser(user);
    setResetResult(null);
    setNewPassword('');
    setResetDialogOpen(true);
  };

  const confirmResetPassword = async () => {
    if (!resetUser) return;
    setResettingPassword(true);
    try {
      const response = await axios.post(`${API}/users/${resetUser.id}/reset-password`, {
        password: newPassword || null  // Send null to auto-generate
      });
      setResetResult(response.data);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Delete user functions
  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${userToDelete.id}`);
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-500/10 text-red-400 border-red-500/30';
      case 'manager':
        return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
      default:
        return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-4 h-4 text-red-400" />;
      case 'manager':
        return <Shield className="w-4 h-4 text-amber-400" />;
      default:
        return <User className="w-4 h-4 text-blue-400" />;
    }
  };

  const canInvite = currentUser?.role === 'admin' || currentUser?.role === 'manager';

  const handlePhoneSave = async (userId, phone) => {
    setSavingPhone({ ...savingPhone, [userId]: true });
    try {
      const formData = new FormData();
      formData.append('phone', phone || '');
      await axios.put(`${API}/users/${userId}/phone`, formData);
      toast.success('Phone number updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update phone');
    } finally {
      setSavingPhone({ ...savingPhone, [userId]: false });
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Team Members</h1>
          <p className="text-muted-foreground mt-1">
            Manage users and their department access
          </p>
        </div>
        
        {canInvite && (
          <Dialog open={inviteDialogOpen} onOpenChange={(open) => { if (!open) resetInviteDialog(); else setInviteDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="btn-glow" data-testid="invite-user-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
                <DialogDescription>
                  Invite a new user and assign them department access.
                </DialogDescription>
              </DialogHeader>
              
              {inviteResult ? (
                <div className="space-y-4 mt-4">
                  <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <p className="text-emerald-400 font-medium mb-2">User Created Successfully!</p>
                    <p className="text-sm text-muted-foreground">
                      {inviteResult.password_was_set 
                        ? `${inviteResult.name} can now login with the password you set.`
                        : `Share these credentials with ${inviteResult.name}:`}
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-secondary space-y-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-mono text-foreground">{inviteResult.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {inviteResult.password_was_set ? 'Password (as set by you)' : 'Temporary Password'}
                      </p>
                      <p className="font-mono text-foreground">{inviteResult.temporary_password}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button onClick={copyCredentials} variant="outline" className="flex-1">
                      {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copied ? 'Copied!' : 'Copy Credentials'}
                    </Button>
                    <Button onClick={resetInviteDialog} className="flex-1">
                      Done
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleInvite} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-name">Full Name</Label>
                    <Input
                      id="invite-name"
                      placeholder="Jane Smith"
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                      required
                      data-testid="invite-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="jane@company.com"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      required
                      data-testid="invite-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                      <SelectTrigger data-testid="invite-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member - Access assigned departments only</SelectItem>
                        <SelectItem value="manager">Manager - Access all departments</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="invite-password">Password (Optional)</Label>
                    <Input
                      id="invite-password"
                      type="password"
                      placeholder="Leave blank to auto-generate"
                      value={inviteForm.password}
                      onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
                      data-testid="invite-password-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      {inviteForm.password 
                        ? (inviteForm.password.length >= 6 
                          ? '✓ Custom password will be used' 
                          : '⚠ Password must be at least 6 characters')
                        : 'A secure password will be auto-generated if left blank'}
                    </p>
                  </div>

                  {inviteForm.role === 'member' && departments.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <FolderKanban className="w-4 h-4" />
                        Department Access
                      </Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Select which departments this user can access
                      </p>
                      <div className="border border-border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                        {departments.map((dept) => (
                          <div key={dept.id} className="flex items-center gap-2">
                            <Checkbox
                              id={`invite-dept-${dept.id}`}
                              checked={inviteForm.department_ids.includes(dept.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setInviteForm({ ...inviteForm, department_ids: [...inviteForm.department_ids, dept.id] });
                                } else {
                                  setInviteForm({ ...inviteForm, department_ids: inviteForm.department_ids.filter(d => d !== dept.id) });
                                }
                              }}
                            />
                            <label htmlFor={`invite-dept-${dept.id}`} className="text-sm text-foreground cursor-pointer flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${CATEGORY_COLORS[dept.category] || 'bg-gray-500/20 text-gray-400'}`}>
                                {dept.name}
                              </span>
                              <span className="text-xs text-muted-foreground">({dept.category})</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={resetInviteDialog}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={inviting} data-testid="send-invite-btn">
                      {inviting ? 'Inviting...' : 'Send Invite'}
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Role Legend */}
      <Card className="bg-secondary/30">
        <CardContent className="p-4">
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-red-400 mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Admin</p>
                <p className="text-muted-foreground text-xs">All departments, full control</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Manager</p>
                <p className="text-muted-foreground text-xs">All departments, can invite users</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Member</p>
                <p className="text-muted-foreground text-xs">Only assigned departments</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{users.length}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{users.filter(u => u.role === 'admin').length}</p>
                <p className="text-sm text-muted-foreground">Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{users.filter(u => u.role === 'manager').length}</p>
                <p className="text-sm text-muted-foreground">Managers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{users.filter(u => u.role === 'member').length}</p>
                <p className="text-sm text-muted-foreground">Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
          <CardDescription>
            Click on a user to view and edit their department access
          </CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No users yet</p>
              <p className="text-sm text-muted-foreground mt-1">Invite your first team member</p>
            </div>
          ) : (
            <div className="space-y-2">
              {users.map((user) => {
                const isExpanded = expandedUser === user.id;
                const userDepartments = user.department_ids || [];
                
                return (
                  <div
                    key={user.id}
                    className="border border-border rounded-lg overflow-hidden"
                    data-testid={`user-${user.id}`}
                  >
                    {/* User Row */}
                    <div 
                      className={`flex items-center gap-4 p-4 cursor-pointer hover:bg-secondary/50 transition-colors ${isExpanded ? 'bg-secondary/30' : ''}`}
                      onClick={() => setExpandedUser(isExpanded ? null : user.id)}
                    >
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-lg font-semibold text-primary">
                          {user.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-foreground">{user.name}</span>
                          <Badge variant="outline" className={getRoleBadgeStyle(user.role)}>
                            {getRoleIcon(user.role)}
                            <span className="ml-1 capitalize">{user.role}</span>
                          </Badge>
                          {user.id === currentUser?.id && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(user.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Department Count & Expand */}
                      <div className="flex items-center gap-4">
                        {user.role === 'member' && (
                          <div className="text-right">
                            <p className="text-sm font-medium text-foreground">
                              {userDepartments.length} department{userDepartments.length !== 1 ? 's' : ''}
                            </p>
                            <p className="text-xs text-muted-foreground">assigned</p>
                          </div>
                        )}
                        {(user.role === 'manager' || user.role === 'admin') && (
                          <div className="text-right">
                            <p className="text-sm font-medium text-primary">All departments</p>
                            <p className="text-xs text-muted-foreground">full access</p>
                          </div>
                        )}
                        
                        {canInvite && user.id !== currentUser?.id && (
                          <div className="flex items-center gap-2">
                            <Select 
                              value={user.role} 
                              onValueChange={(value) => handleRoleChange(user.id, value)}
                            >
                              <SelectTrigger className="w-28" data-testid={`role-select-${user.id}`} onClick={(e) => e.stopPropagation()}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="member">Member</SelectItem>
                                <SelectItem value="manager">Manager</SelectItem>
                                {currentUser?.role === 'admin' && (
                                  <SelectItem value="admin">Admin</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {/* Admin Action Buttons */}
                        {isAdmin && user.id !== currentUser?.id && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handleEditUser(user)}
                              title="Edit user"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handleResetPassword(user)}
                              title="Reset password"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                              onClick={() => handleDeleteUser(user)}
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Department Access Section */}
                    {isExpanded && (
                      <div className="border-t border-border p-4 bg-secondary/20">
                        {/* Phone Number for SMS Notifications (Managers/Admins) */}
                        {(user.role === 'admin' || user.role === 'manager') && (
                          <div className="mb-4 pb-4 border-b border-border">
                            <div className="flex items-center gap-2 mb-2">
                              <Phone className="w-4 h-4 text-emerald-400" />
                              <span className="font-medium text-foreground">WhatsApp Notifications</span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">
                              Phone number for receiving override request alerts via WhatsApp
                            </p>
                            <div className="flex gap-2">
                              <Input
                                type="tel"
                                placeholder="+1234567890"
                                value={editingPhone[user.id] !== undefined ? editingPhone[user.id] : (user.phone || '')}
                                onChange={(e) => setEditingPhone({ ...editingPhone, [user.id]: e.target.value })}
                                className="flex-1"
                              />
                              <Button 
                                size="sm"
                                onClick={() => handlePhoneSave(user.id, editingPhone[user.id] !== undefined ? editingPhone[user.id] : user.phone)}
                                disabled={savingPhone[user.id]}
                              >
                                {savingPhone[user.id] ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Save className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-2 mb-3">
                          <FolderKanban className="w-4 h-4 text-primary" />
                          <span className="font-medium text-foreground">Department Access</span>
                        </div>
                        
                        {user.role === 'admin' || user.role === 'manager' ? (
                          <div>
                            <p className="text-sm text-muted-foreground mb-3">
                              {user.role === 'admin' ? 'Admins' : 'Managers'} have access to all departments automatically.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {departments.map((dept) => (
                                <span
                                  key={dept.id}
                                  className={`px-2 py-1 rounded-full text-xs ${CATEGORY_COLORS[dept.category] || 'bg-gray-500/20 text-gray-400'}`}
                                >
                                  {dept.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : departments.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No departments available. Create a department first.</p>
                        ) : (
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {departments.map((dept) => {
                              const hasAccess = userDepartments.includes(dept.id);
                              return (
                                <div
                                  key={dept.id}
                                  className={`flex items-center gap-2 p-3 rounded-lg border ${
                                    hasAccess ? 'bg-primary/5 border-primary/30' : 'bg-secondary/30 border-border'
                                  }`}
                                >
                                  {canInvite && user.id !== currentUser?.id ? (
                                    <Checkbox
                                      id={`user-${user.id}-dept-${dept.id}`}
                                      checked={hasAccess}
                                      onCheckedChange={() => handleDepartmentToggle(user.id, dept.id, userDepartments)}
                                    />
                                  ) : (
                                    <div className={`w-4 h-4 rounded-full ${hasAccess ? 'bg-primary' : 'bg-muted'}`} />
                                  )}
                                  <label 
                                    htmlFor={`user-${user.id}-dept-${dept.id}`}
                                    className={`text-sm cursor-pointer flex-1 ${hasAccess ? 'text-foreground' : 'text-muted-foreground'}`}
                                  >
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${CATEGORY_COLORS[dept.category] || 'bg-gray-500/20 text-gray-400'}`}>
                                      {dept.name}
                                    </span>
                                  </label>
                                  {hasAccess && (
                                    <Check className="w-4 h-4 text-primary" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Edit User
            </DialogTitle>
            <DialogDescription>
              Update user details for {editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter email"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveUserEdit} disabled={savingEdit}>
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={(open) => {
        setResetDialogOpen(open);
        if (!open) {
          setResetResult(null);
          setNewPassword('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-400" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              {resetResult 
                ? 'Password has been reset successfully.'
                : `Reset password for ${resetUser?.name || resetUser?.email}?`}
            </DialogDescription>
          </DialogHeader>
          
          {resetResult ? (
            <div className="space-y-4 py-4">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-2">
                  {resetResult.password_was_set ? 'New Password (as set by you):' : 'New Temporary Password:'}
                </p>
                <p className="font-mono text-lg font-bold text-emerald-400">{resetResult.temporary_password}</p>
              </div>
              <Button onClick={copyResetCredentials} className="w-full gap-2">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reset-password">New Password (Optional)</Label>
                <Input
                  id="reset-password"
                  type="password"
                  placeholder="Leave blank to auto-generate"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="reset-password-input"
                />
                <p className="text-xs text-muted-foreground">
                  {newPassword 
                    ? (newPassword.length >= 6 
                      ? '✓ Custom password will be used' 
                      : '⚠ Password must be at least 6 characters')
                    : 'A secure password will be auto-generated if left blank'}
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="default" 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={confirmResetPassword} 
                  disabled={resettingPassword || (newPassword && newPassword.length < 6)}
                >
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {userToDelete?.name || userToDelete?.email}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-foreground font-medium">This action cannot be undone</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    The user will lose access immediately. Their form submissions will be preserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteUser} 
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
